// client/src/components/secure/PublicListingPanel.js — how one of our listings appears on
// the public website: the seller's internet-display choices, county, the commercial facts
// (zoning, cap rate, lease basis and terms), and one click to the public page or the
// print flyer. Saves through PATCH /api/properties/:id/marketing; the website's listing
// index updates at once.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const PUBLIC = ['For Sale', 'For Lease', 'Pending', 'Sold', 'Rented'];

const PublicListingPanel = ({ property, onSaved }) => {
    const api = useApiClient();
    const has = property.internet_display !== undefined;
    const [f, setF] = useState({
        internet_display: property.internet_display === undefined ? 1 : property.internet_display,
        address_display: property.address_display === undefined ? 1 : property.address_display,
        county: property.county || '', zoning: property.zoning || '', cap_rate: property.cap_rate ?? '',
        lease_rate_basis: property.lease_rate_basis || '', lease_terms: property.lease_terms || '',
    });
    const [msg, setMsg] = useState('');
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value }));
    const save = async () => {
        setMsg('');
        try { const p = await api.patch(`/api/properties/${property.property_id}/marketing`, f); setMsg('Saved — the website is updated.'); if (onSaved) onSaved(p); }
        catch (e) { setMsg(e.message); }
    };
    const onSite = PUBLIC.includes(property.status) && Number(f.internet_display) === 1;
    const inp = 'p-2 border rounded text-sm w-full';
    return (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex flex-wrap justify-between gap-2 items-center mb-3">
                <h2 className="text-xl font-bold text-slate-800">Website &amp; marketing</h2>
                <div className="flex gap-3 text-sm">
                    {onSite && <a className="text-indigo-700 underline" href={`/properties/own-${property.property_id}`} target="_blank" rel="noreferrer">Public page</a>}
                    <Link className="text-indigo-700 underline" to={`/flyer/${property.property_id}`}>Print flyer</Link>
                </div>
            </div>
            {!has && <p className="text-sm text-amber-800 bg-amber-50 rounded p-2 mb-2">These settings arrive with the property search update (migration 2026_10_03); until it is run every active listing shows with its address.</p>}
            <div className="grid md:grid-cols-2 gap-3 text-sm">
                <label className="flex gap-2 items-center"><input type="checkbox" checked={Number(f.internet_display) === 1} onChange={set('internet_display')} /> Show this listing on the website (the seller has not opted out)</label>
                <label className="flex gap-2 items-center"><input type="checkbox" checked={Number(f.address_display) === 1} onChange={set('address_display')} /> Show the street address</label>
                <label>County<input className={inp} value={f.county} onChange={set('county')} /></label>
                <label>Zoning<input className={inp} value={f.zoning} onChange={set('zoning')} /></label>
                <label>Cap rate (%)<input className={inp} value={f.cap_rate} onChange={set('cap_rate')} inputMode="decimal" /></label>
                <label>Lease rate quoted
                    <select className={inp} value={f.lease_rate_basis} onChange={set('lease_rate_basis')}>
                        <option value="">—</option><option value="per_month">per month</option><option value="per_year">per year</option>
                        <option value="per_sf_year">per sq ft per year</option><option value="per_sf_month">per sq ft per month</option>
                    </select></label>
                <label>Lease type<input className={inp} value={f.lease_terms} onChange={set('lease_terms')} placeholder="NNN, gross, modified gross" /></label>
            </div>
            <div className="mt-3 flex gap-3 items-center">
                <button type="button" onClick={save} className="px-4 py-2 text-sm text-white bg-slate-800 rounded-md hover:bg-slate-900">Save</button>
                {msg && <span className="text-sm" role="status">{msg}</span>}
            </div>
            {!PUBLIC.includes(property.status) && <p className="mt-2 text-xs text-slate-500">Only For Sale, For Lease and Pending listings (and Sold/Rented, if the broker allows sold listings) appear on the website.</p>}
        </div>
    );
};

export default PublicListingPanel;
